import { create } from "zustand";
import { WalletController } from "@api/WalletController";
import { ISwapExternalMessagesResponsePayload, ITransactionResponse } from "@api/IWalletController";
import { useWallet } from "./wallet.store";
import { usePools } from "./pools.store";
import { usePair } from "./pair.store"; // Import the pair store
import { IAsset } from "@api/IWalletController";
import { initialAssets } from "./assets.store";

// Define asset types
export enum AssetType {
    NATIVE = "ton",
    JETTON = "jetton",
}

// Default assets to initialize the store with

// Fully consolidated asset information
export interface AssetInfo {
    asset: IAsset;
    balance: number;
    amount: string;
    price: number | null;
    setAsset: (asset: IAsset) => void;
}

// Swap store interface with consolidated state
interface SwapState {
    availableAssets: IAsset[];
    sellAssetInfo: AssetInfo;
    buyAssetInfo: AssetInfo;
    selectedPool: string | null;
    isLoading: boolean;
    error: string | null;
    slippagePercentage: number;
}

// Swap store interface
interface ISwap extends SwapState {
    // Actions
    isBalanceSufficient: () => boolean;
    setAvailableAssets: (assets: IAsset[]) => void;
    setSlippagePercentage: (slippagePercentage: number) => void;
    setBuyAsset: (asset: IAsset) => void;
    setSellAsset: (asset: IAsset) => void;
    setSwapAmount: (amount: string, flip?: boolean) => void;
    setSelectedPool: (poolDex: string) => void;
    setSellAssetUSDPrice: (price: number) => void;
    setBuyAssetUSDPrice: (price: number) => void;
    flipAssets: () => void;
    preSwapHandler: () => Promise<boolean>;
    executeSwap: () => Promise<ISwapExternalMessagesResponsePayload[] | ITransactionResponse | null>;
    updateAssetBalances: () => Promise<void>;
    canSwap: () => boolean;
    updateSellAssetBalance: () => Promise<void>;
    updateBuyAssetBalance: () => Promise<void>;
    updateAssetPrices: () => void;
}

// Create the initial state
const createInitialState = (): SwapState => ({
    availableAssets: initialAssets,
    sellAssetInfo: {
        asset: initialAssets[0],
        balance: 0,
        amount: "1", // Set initial sell amount to 1
        price: null,
        setAsset: () => {}, // Placeholder, will be implemented in the store
    },
    buyAssetInfo: {
        asset: initialAssets[1],
        balance: 0,
        amount: "",
        price: null, // Will be set dynamically from pair info or fallback API
        setAsset: () => {}, // Placeholder, will be implemented in the store
    },
    selectedPool: null,
    isLoading: false,
    error: null,
    slippagePercentage: 0.5,
});

export const useSwap = create<ISwap>()((set, get) => {
    // Initialize with proper implementations of the setAsset functions
    const state = createInitialState();

    return {
        ...state,
        sellAssetInfo: {
            ...state.sellAssetInfo,
            setAsset: (asset: IAsset) => {
                set((state: ISwap) => ({
                    sellAssetInfo: {
                        ...state.sellAssetInfo,
                        asset,
                        price: null, // Always reset price so it is fetched from API
                    },
                }));
                setTimeout(() => {
                    get().preSwapHandler();
                }, 0);
            },
        },
        buyAssetInfo: {
            ...state.buyAssetInfo,
            setAsset: (asset: IAsset) => {
                set((state: ISwap) => ({
                    buyAssetInfo: {
                        ...state.buyAssetInfo,
                        asset,
                        price: null, // Always reset price so it is fetched from API
                    },
                }));
                setTimeout(() => {
                    get().preSwapHandler();
                }, 0);
            },
        },

        // Simple state setters
        setSlippagePercentage: (slippagePercentage: number) => set({ slippagePercentage }),
        setAvailableAssets: (assets: IAsset[]) => set({ availableAssets: assets }),
        setSellAssetUSDPrice: (price: number) =>
            set((state) => ({
                sellAssetInfo: {
                    ...state.sellAssetInfo,
                    price,
                },
            })),
        setBuyAssetUSDPrice: (price: number) =>
            set((state) => ({
                buyAssetInfo: {
                    ...state.buyAssetInfo,
                    price,
                },
            })),
        setSelectedPool: (poolDex: string) => {
            set({ selectedPool: poolDex });

            // Update prices when pool changes
            setTimeout(() => {
                get().updateAssetPrices();

                // Recalculate rates if we have a valid amount
                const { sellAssetInfo, buyAssetInfo } = get();
                if (sellAssetInfo.asset && buyAssetInfo.asset && sellAssetInfo.amount) {
                    const numericAmount = parseFloat(sellAssetInfo.amount);
                    if (!isNaN(numericAmount) && numericAmount > 0) {
                        // Trigger rate recalculation
                        usePools
                            .getState()
                            .queueRateCalculation(sellAssetInfo.asset, buyAssetInfo.asset, sellAssetInfo.amount);
                    }
                }
            }, 0);
        },

        // Computed values
        isBalanceSufficient: () => {
            const { sellAssetInfo } = get();
            const numericAmount = parseFloat(sellAssetInfo.amount);
            return !isNaN(numericAmount) ? numericAmount <= sellAssetInfo.balance : true;
        },

        // Check if swap is currently possible
        canSwap: () => {
            const { sellAssetInfo, isLoading, selectedPool } = get();
            const poolsStore = usePools.getState();

            // Check if swap amount is valid
            const numericAmount = parseFloat(sellAssetInfo.amount);
            const isAmountValid = !isNaN(numericAmount) && numericAmount > 0 && sellAssetInfo.amount.length > 0;

            // Check if balance is sufficient
            const isBalanceOk = get().isBalanceSufficient();

            // Check if pools are still loading
            const arePoolsLoading = poolsStore.isLoading;

            // Check if we have emulation results
            const hasEmulationResults =
                poolsStore.emulationResults &&
                Array.isArray(poolsStore.emulationResults) &&
                poolsStore.emulationResults.length > 0;

            // Check if a pool is selected
            const isPoolSelected = selectedPool !== null;

            // Swap is possible when:
            // 1. Amount is valid
            // 2. Balance is sufficient
            // 3. Not currently loading
            // 4. Pools are not loading
            // 5. We have emulation results
            // 6. A pool is selected
            return Boolean(
                isAmountValid && isBalanceOk && !isLoading && !arePoolsLoading && hasEmulationResults && isPoolSelected,
            );
        },

        // Asset selection handlers
        setSellAsset: (asset: IAsset) => {
            // Reset amount when asset changes
            const { sellAssetInfo } = get();
            const shouldResetAmount = sellAssetInfo.asset?.guid !== asset.guid;

            set((state) => ({
                sellAssetInfo: {
                    ...state.sellAssetInfo,
                    asset,
                    amount: shouldResetAmount ? "" : state.sellAssetInfo.amount,
                },
            }));

            // Update balances and trigger full recalculation
            get().updateSellAssetBalance();
            get().preSwapHandler();
            get().updateAssetPrices();
        },

        setBuyAsset: (asset: IAsset) => {
            // Reset amount when asset changes
            const { buyAssetInfo } = get();
            const shouldResetAmount = buyAssetInfo.asset?.guid !== asset.guid;

            set((state) => ({
                buyAssetInfo: {
                    ...state.buyAssetInfo,
                    asset,
                    amount: shouldResetAmount ? "" : state.buyAssetInfo.amount,
                },
            }));

            // Update balances and trigger full recalculation
            get().updateBuyAssetBalance();
            get().preSwapHandler();
            get().updateAssetPrices();
        },

        // Swap assets (flip buy/sell)
        flipAssets: () => {
            const { sellAssetInfo, buyAssetInfo } = get();
            set({
                sellAssetInfo: {
                    ...buyAssetInfo,
                    amount: sellAssetInfo.amount, // Keep the amount from sell asset
                },
                buyAssetInfo: {
                    ...sellAssetInfo,
                    amount: "", // Clear the buy amount
                },
            });
        },

        // Swap amount handler
        setSwapAmount: (amount: string, flip: boolean = false) => {
            set((state) => ({
                sellAssetInfo: {
                    ...state.sellAssetInfo,
                    amount,
                },
            }));

            // If we have a valid amount, trigger the swap emulation
            const numericAmount = parseFloat(amount);
            if (!isNaN(numericAmount) && numericAmount > 0) {
                const { sellAssetInfo, buyAssetInfo } = get();
                if (sellAssetInfo.asset && buyAssetInfo.asset) {
                    if (!flip) {
                        // Use the queueRateCalculation function for rate calculation
                        usePools.getState().queueRateCalculation(sellAssetInfo.asset, buyAssetInfo.asset, amount);
                    }
                }
            }
        },

        // Update balances for current assets
        updateAssetBalances: async () => {
            const { sellAssetInfo, buyAssetInfo } = get();
            const { currentWallet } = useWallet.getState();

            if (!sellAssetInfo.asset || !buyAssetInfo.asset || !currentWallet?.guid) return;

            try {
                const response = await WalletController.getBalances(currentWallet.guid, [
                    sellAssetInfo.asset,
                    buyAssetInfo.asset,
                ]);

                if (response.status === "success" && response.data.length > 0) {
                    const assetBalance = response.data[0];

                    if (assetBalance.balances && Array.isArray(assetBalance.balances)) {
                        // Find balances for each asset
                        const sellAssetBalanceItem = assetBalance.balances.find(
                            (b) => b.jettonMasterAddress === sellAssetInfo.asset.jettonMasterAddress,
                        );

                        const buyAssetBalanceItem = assetBalance.balances.find(
                            (b) => b.jettonMasterAddress === buyAssetInfo.asset.jettonMasterAddress,
                        );

                        set((state) => ({
                            sellAssetInfo: {
                                ...state.sellAssetInfo,
                                balance: sellAssetBalanceItem?.amount || 0,
                            },
                            buyAssetInfo: {
                                ...state.buyAssetInfo,
                                balance: buyAssetBalanceItem?.amount || 0,
                            },
                        }));
                    }
                }
            } catch (error) {
                console.error("Error updating swap asset balances:", error);
            }
        },

        updateSellAssetBalance: async () => {
            const { sellAssetInfo } = get();
            const { currentWallet } = useWallet.getState();

            if (!sellAssetInfo.asset || !currentWallet?.guid) return;

            try {
                const response = await WalletController.getBalances(currentWallet.guid, [sellAssetInfo.asset]);

                if (response.status === "success" && response.data.length > 0) {
                    const assetBalance = response.data[0];
                    // Find the balance for this asset in the balances array
                    let balance = 0;
                    if (assetBalance.balances && Array.isArray(assetBalance.balances)) {
                        const assetBalanceItem = assetBalance.balances.find(
                            (b) => b.jettonMasterAddress === sellAssetInfo.asset.jettonMasterAddress,
                        );
                        if (assetBalanceItem) {
                            balance = assetBalanceItem.amount;
                        }
                    }

                    set((state) => ({
                        sellAssetInfo: {
                            ...state.sellAssetInfo,
                            balance,
                        },
                    }));
                }
            } catch (error) {
                console.error("Error updating sell asset balance:", error);
            }
        },

        updateBuyAssetBalance: async () => {
            const { buyAssetInfo } = get();
            const { currentWallet } = useWallet.getState();

            if (!buyAssetInfo.asset || !currentWallet?.guid) return;

            try {
                const response = await WalletController.getBalances(currentWallet.guid, [buyAssetInfo.asset]);

                if (response.status === "success" && response.data.length > 0) {
                    const assetBalance = response.data[0];
                    // Find the balance for this asset in the balances array
                    let balance = 0;
                    if (assetBalance.balances && Array.isArray(assetBalance.balances)) {
                        const assetBalanceItem = assetBalance.balances.find(
                            (b) => b.jettonMasterAddress === buyAssetInfo.asset.jettonMasterAddress,
                        );
                        if (assetBalanceItem) {
                            balance = assetBalanceItem.amount;
                        }
                    }

                    set((state) => ({
                        buyAssetInfo: {
                            ...state.buyAssetInfo,
                            balance,
                        },
                    }));
                }
            } catch (error) {
                console.error("Error updating buy asset balance:", error);
            }
        },

        // Update USD prices based on the selected pool
        updateAssetPrices: async () => {
            const { selectedPool, sellAssetInfo, buyAssetInfo } = get();
            const { pools } = usePools.getState();
            let tonUsdPrice: number | null = null;

            if (!selectedPool) return;

            // Find the selected pool data to get the pool address
            const poolData = pools.find((p) => p.dex === selectedPool);
            if (!poolData) return;

            // First, make sure the pair info is fetched for this pool
            await usePair.getState().fetchPairInfo(poolData.poolAddress);

            // Then get the USD prices from the pair store
            let { sellAssetUsdPrice, buyAssetUsdPrice } = usePair.getState().fetchPairUsdPriceInfo();

            // Fallback: If TON price is missing, fetch from TonApi
            async function fetchTonUsdPriceFallback(): Promise<number | null> {
                try {
                    const { TonApi } = await import("@api/TonApi");
                    const tonApi = new TonApi();
                    const rates = await tonApi.getTonRates();
                    if (rates && rates.ton && rates.ton.prices && rates.ton.prices.USD) {
                        return rates.ton.prices.USD;
                    }
                } catch (e) {
                    console.error("TON USD price fallback error:", e);
                }
                return null;
            }

            // Check sell asset (TON fallback)
            if (
                sellAssetInfo.asset.symbol?.toLowerCase() === "ton" &&
                (sellAssetUsdPrice === null || typeof sellAssetUsdPrice === "undefined")
            ) {
                tonUsdPrice = await fetchTonUsdPriceFallback();
                if (tonUsdPrice !== null) sellAssetUsdPrice = tonUsdPrice;
            }
            // Check buy asset (TON fallback)
            if (
                buyAssetInfo.asset.symbol?.toLowerCase() === "ton" &&
                (buyAssetUsdPrice === null || typeof buyAssetUsdPrice === "undefined")
            ) {
                if (!tonUsdPrice) tonUsdPrice = await fetchTonUsdPriceFallback();
                if (tonUsdPrice !== null) buyAssetUsdPrice = tonUsdPrice;
            }

            // Update the sell and buy asset prices
            if (sellAssetUsdPrice !== null) {
                set((state) => ({
                    sellAssetInfo: {
                        ...state.sellAssetInfo,
                        price: sellAssetUsdPrice,
                    },
                }));
            }

            if (buyAssetUsdPrice !== null) {
                set((state) => ({
                    buyAssetInfo: {
                        ...state.buyAssetInfo,
                        price: buyAssetUsdPrice,
                    },
                }));
            }
        },

        // Prepare for swap by fetching balances and pools
        preSwapHandler: async () => {
            const { sellAssetInfo, buyAssetInfo } = get();
            const { currentWallet } = useWallet.getState();
            const poolsStore = usePools.getState();

            // Validate assets
            if (!sellAssetInfo.asset || !buyAssetInfo.asset) {
                set({ error: "Please select assets for swap" });
                return false;
            }

            // Set loading state
            set({ isLoading: true, error: null });

            try {
                // Update balances if wallet is connected
                if (currentWallet?.guid) {
                    await get().updateAssetBalances();
                }

                // Always fetch fresh pools and rates when assets change
                const success = await poolsStore.fetchLiquidityPools(
                    sellAssetInfo.asset,
                    buyAssetInfo.asset,
                    sellAssetInfo.amount,
                );

                // Update asset prices based on the selected pool
                get().updateAssetPrices();

                // If we have a valid amount, trigger rate recalculation
                const numericAmount = parseFloat(sellAssetInfo.amount);
                if (!isNaN(numericAmount) && numericAmount > 0) {
                    poolsStore.queueRateCalculation(sellAssetInfo.asset, buyAssetInfo.asset, sellAssetInfo.amount);
                }

                set({ isLoading: false });
                return success;
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : "Unknown error during swap preparation";
                set({
                    error: errorMessage,
                    isLoading: false,
                });
                console.error("Error in preSwapHandler:", error);
                return false;
            }
        },

        // Execute the swap transaction
        executeSwap: async () => {
            const state = get();
            const { sellAssetInfo, buyAssetInfo, selectedPool } = state;
            const { currentWallet } = useWallet.getState();
            const { pools } = usePools.getState();

            if (!sellAssetInfo.asset || !buyAssetInfo.asset || !selectedPool) {
                set({ error: "Missing required swap parameters" });
                return null;
            }

            if (!currentWallet?.guid) {
                set({ error: "Wallet not connected" });
                return null;
            }

            set({ isLoading: true, error: null });

            try {
                // Find the selected pool data
                const poolData = pools.find((p) => p.dex === selectedPool);
                if (!poolData) {
                    set({ error: "Selected pool not found" });
                    return null;
                }

                

                // Execute the swap using the wallet controller
                let response;
                if (currentWallet.isMnemonics) {
                    // Internal wallet swap
                    response = await WalletController.swapInternal({
                        walletGuid: currentWallet.guid,
                        poolAddress: poolData.poolAddress,
                        assetInGuid: sellAssetInfo.asset.guid,
                        assetOutGuid: buyAssetInfo.asset.guid,
                        amount: parseFloat(sellAssetInfo.amount), // Convert string to number
                        slippagePercentage: state.slippagePercentage.toString(), // Convert number to string
                    });
                } else {
                    // External wallet swap (Tonkeeper/TonConnect)
                    response = await WalletController.swapExternal({
                        walletGuid: currentWallet.guid,
                        poolAddress: poolData.poolAddress,
                        assetInGuid: sellAssetInfo.asset.guid,
                        assetOutGuid: buyAssetInfo.asset.guid,
                        amount: parseFloat(sellAssetInfo.amount), // Convert string to number
                        slippagePercentage: state.slippagePercentage.toString(), // Convert number to string
                    });
                }

                if (response.status === "success") {
                    // Reset swap amount after successful swap
                    state.setSwapAmount("");

                    // Refresh balances
                    state.updateSellAssetBalance();
                    state.updateBuyAssetBalance();

                    return response.data;
                } else {
                    set({ error: response.message || "Swap failed" });
                    return null;
                }
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : "Unknown error during swap";
                set({ error: errorMessage });
                console.error("Error executing swap:", error);
                return null;
            } finally {
                set({ isLoading: false });
            }
        },
    };
});
